import { useEffect, useRef, useState } from "react";

/**
 * AnimatedLogo — inline SVG mark of AfroRichMind with a one-time entrance:
 *   1) Rays draw in
 *   2) Bars grow from base with a staggered lift
 *   3) Coin rises + settles, trend line traces, endpoint pops
 *
 * Props:
 *   size      number (px)                — square dimension.  default 40
 *   replay    boolean                    — animate every mount.  default false
 *   className string                     — extra classes on wrapper
 *   glow      boolean                    — soft gold glow behind. default true
 */
export default function AnimatedLogo({ size = 40, replay = false, className = "", glow = true }) {
  const ref = useRef(null);
  const [play, setPlay] = useState(false);

  useEffect(() => {
    if (replay) { setPlay(true); return; }
    // Play once per browser session so navigation doesn't feel jumpy
    const KEY = "arm_logo_played";
    if (!sessionStorage.getItem(KEY)) {
      sessionStorage.setItem(KEY, "1");
      setPlay(true);
    }
  }, [replay]);

  return (
    <span
      ref={ref}
      data-testid="animated-logo"
      className={`inline-block relative ${className}`}
      style={{ width: size, height: size }}
    >
      {glow && (
        <span
          aria-hidden
          className="absolute inset-0 rounded-lg pointer-events-none"
          style={{
            background: "radial-gradient(closest-side, rgba(212,175,55,0.35), rgba(212,175,55,0) 70%)",
            filter: "blur(6px)",
          }}
        />
      )}
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 64 64"
        width={size}
        height={size}
        className={`relative rounded-lg ${play ? "arm-logo-play" : ""}`}
        role="img"
        aria-label="AfroRichMind"
      >
        <defs>
          <linearGradient id="arm-g" x1="0" y1="0" x2="1" y2="1">
            <stop offset="0%" stopColor="#F6D77A" />
            <stop offset="55%" stopColor="#D4AF37" />
            <stop offset="100%" stopColor="#8A6511" />
          </linearGradient>
          <linearGradient id="arm-b" x1="0" y1="1" x2="0" y2="0">
            <stop offset="0%" stopColor="#B38728" />
            <stop offset="100%" stopColor="#F6D77A" />
          </linearGradient>
        </defs>

        <rect x="0" y="0" width="64" height="64" rx="14" fill="#0F1014" />
        <rect x="0" y="0" width="64" height="64" rx="14" fill="none"
              stroke="url(#arm-g)" strokeWidth="1" strokeOpacity="0.35" />

        {/* rays */}
        <g className="arm-rays" stroke="url(#arm-g)" strokeWidth="1.4" strokeLinecap="round" opacity="0.9">
          <line x1="32" y1="6"  x2="32" y2="11"   className="arm-ray arm-ray-1" />
          <line x1="19" y1="10" x2="22" y2="14.5" className="arm-ray arm-ray-2" />
          <line x1="45" y1="10" x2="42" y2="14.5" className="arm-ray arm-ray-3" />
        </g>

        {/* coin */}
        <g className="arm-coin">
          <circle cx="32" cy="20" r="7" fill="url(#arm-g)" />
          <text x="32" y="23.3" textAnchor="middle"
                fontFamily="Georgia, serif" fontWeight="700" fontSize="9" fill="#0F1014">A</text>
        </g>

        {/* bars — origin-bottom on each rect via transform-box */}
        <g className="arm-bars">
          <rect className="arm-bar arm-bar-1" x="14"   y="38" width="6.5" height="16" rx="1.6" fill="url(#arm-b)" opacity="0.55" />
          <rect className="arm-bar arm-bar-2" x="23"   y="33" width="6.5" height="21" rx="1.6" fill="url(#arm-b)" opacity="0.8" />
          <rect className="arm-bar arm-bar-3" x="34.5" y="28" width="6.5" height="26" rx="1.6" fill="url(#arm-b)" />
          <rect className="arm-bar arm-bar-4" x="43.5" y="34" width="6.5" height="20" rx="1.6" fill="url(#arm-b)" opacity="0.6" />
        </g>

        {/* trend line traced with dashoffset */}
        <polyline
          className="arm-trend"
          points="14,40 26,36 37,30 46,36"
          fill="none" stroke="#F6D77A" strokeWidth="1.4"
          strokeLinecap="round" strokeLinejoin="round"
        />
        <circle className="arm-dot" cx="37" cy="30" r="1.4" fill="#F6D77A" />

        {/* base line */}
        <rect className="arm-base" x="12" y="55" width="40" height="1.2" rx="0.6" fill="url(#arm-g)" opacity="0.7" />
      </svg>
    </span>
  );
}
